import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useSchoolSearch, useClassSearch, useProfessorSearch, usePopularClasses,
  useCreateSchool, useCreateClass, useCreateProfessor, useCreateSection, useEnrollSection,
} from '../lib/api';
import { Combobox, type ComboboxItem } from '../components/Combobox';
import type { School, ClassCode, Professor } from '@shared/types';

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 2020 + 2 }, (_, i) => 2020 + i).reverse();
const TERMS = ['Winter', 'Spring', 'Summer', 'Fall'];

export function OnboardingPage() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);

  const [school, setSchool] = useState<School | null>(null);
  const [classCode, setClassCode] = useState<ClassCode | null>(null);
  const [professor, setProfessor] = useState<Professor | null>(null);
  const [allTeachers, setAllTeachers] = useState(false);
  const [year, setYear] = useState<number | null>(null);
  const [term, setTerm] = useState<string | null>(null);
  const [sectionId, setSectionId] = useState<string | null>(null);

  const createSection = useCreateSection();
  const enroll = useEnrollSection();

  const skipProfessor = /\bnational\s+university\b/i.test(school?.name ?? '');
  const totalSteps = skipProfessor ? 5 : 6;

  const handleFinishSetup = async () => {
    if (!classCode || !year) return;
    const termStr = term ? `${term} ${year}` : `${year}`;
    const section = await createSection.mutateAsync({
      classCodeId: classCode.id,
      professorId: allTeachers ? null : (professor?.id ?? null),
      term: termStr,
      examDate: null,
    });
    await enroll.mutateAsync(section.section.id);
    setSectionId(section.section.id);
    setStep(step + 1);
  };

  const progressPercent = Math.round(((step - 1) / totalSteps) * 100);

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col">
      {/* Progress bar */}
      <div className="h-1.5 bg-slate-100">
        <div className="h-full bg-gradient-to-r from-squishy-primary to-teal-400 transition-all duration-500" style={{ width: `${progressPercent}%` }} />
      </div>

      {/* Content */}
      <div className="flex-1 flex items-start justify-center px-6 pt-6 pb-32 overflow-y-auto sm:items-center sm:py-8">
        <div className="w-full max-w-md">
          {step === 1 && (
            <SchoolStep
              school={school}
              onSelect={(s) => { setSchool(s); setStep(2); }}
              onBack={() => nav('/')}
            />
          )}
          {step === 2 && school && (
            <ClassStep
              school={school}
              classCode={classCode}
              onSelect={(c) => { setClassCode(c); setStep(skipProfessor ? 4 : 3); }}
              onBack={() => setStep(1)}
            />
          )}
          {step === 3 && !skipProfessor && school && (
            <ProfessorStep
              school={school}
              onSelect={(p, all) => { setProfessor(p); setAllTeachers(all); setStep(4); }}
              onBack={() => setStep(2)}
            />
          )}
          {step === 4 && (
            <YearStep
              year={year}
              onSelect={(y) => { setYear(y); setStep(5); }}
              onBack={() => setStep(skipProfessor ? 2 : 3)}
            />
          )}
          {step === 5 && (
            <TermStep
              term={term}
              onSelect={(t) => { setTerm(t); handleFinishSetup(); }}
              onSkip={() => { setTerm(null); handleFinishSetup(); }}
              onBack={() => setStep(4)}
              loading={createSection.isPending || enroll.isPending}
            />
          )}
          {step === 6 && sectionId && classCode && (
            <DoneStep
              sectionId={sectionId}
              classCode={classCode.code}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 1: School ──────────────────────────────────────────────────────────

function SchoolStep({ school, onSelect, onBack }: {
  school: School | null;
  onSelect: (s: School) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState(school?.name ?? '');
  const [showCityState, setShowCityState] = useState(false);
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const search = useSchoolSearch(q);
  const create = useCreateSchool();

  const items: ComboboxItem[] = (search.data?.schools ?? []).map((s) => ({
    id: s.id,
    label: s.name,
    sublabel: [s.city, s.state].filter(Boolean).join(', ') || undefined,
  }));

  const handleCreate = async () => {
    if (!showCityState) { setShowCityState(true); return; }
    const r = await create.mutateAsync({ name: q.trim(), city: city.trim() || undefined, state: state.trim() || undefined });
    if (r.school) onSelect(r.school);
  };

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-slate-600">&larr; Back</button>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">What school do you go to?</h1>
        <p className="text-slate-500 mt-2">Start typing and we'll find it for you.</p>
      </div>
      <Combobox
        placeholder="Search your school..."
        value={q}
        onChange={(v) => { setQ(v); setShowCityState(false); }}
        results={items}
        loading={search.isLoading}
        mobileInlineResults
        onSelect={(item) => {
          const found = search.data?.schools?.find((s) => s.id === item.id);
          onSelect(found ?? { id: item.id, name: item.label, city: null, state: null, country: 'US', domain: null, verified: false });
        }}
        onCreate={handleCreate}
        createLabel={showCityState ? undefined : 'Add school'}
      />
      {showCityState && (
        <div className="space-y-3 border rounded-xl p-4 bg-slate-50">
          <p className="text-sm text-slate-600">Where is <strong>{q.trim()}</strong> located?</p>
          <input value={city} onChange={(e) => setCity(e.target.value)}
            placeholder="City (e.g. San Diego)"
            className="w-full border rounded-xl px-4 py-3 text-sm" />
          <input value={state} onChange={(e) => setState(e.target.value)}
            placeholder="State (e.g. California)"
            className="w-full border rounded-xl px-4 py-3 text-sm" />
          <button
            onClick={handleCreate}
            disabled={create.isPending || !q.trim()}
            className="w-full bg-squishy-primary text-white font-semibold py-3 rounded-xl text-base disabled:opacity-50 active:scale-[0.98] transition-transform"
          >
            {create.isPending ? 'Adding...' : `Add "${q.trim()}"`}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Step 2: Class ───────────────────────────────────────────────────────────

function ClassStep({ school, classCode, onSelect, onBack }: {
  school: School;
  classCode: ClassCode | null;
  onSelect: (c: ClassCode) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState(classCode?.code ?? '');
  const search = useClassSearch(school.id, q);
  const popular = usePopularClasses(school.id);
  const create = useCreateClass();

  // Show popular classes when search is empty, search results otherwise
  const showPopular = q.length === 0 && (popular.data?.classes?.length ?? 0) > 0;
  const items: ComboboxItem[] = showPopular
    ? (popular.data?.classes ?? []).map((c) => ({
        id: c.id, label: c.code, sublabel: c.name ? `${c.name} (popular)` : 'Popular',
      }))
    : (search.data?.classes ?? []).map((c) => ({
        id: c.id, label: c.code, sublabel: c.name ?? undefined,
      }));

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-slate-600">&larr; Back</button>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">What class are you taking?</h1>
        <p className="text-slate-500 mt-2">
          Type the class code or name, like <span className="font-medium text-slate-700">NSG 250</span>, <span className="font-medium text-slate-700">PHYS 101</span>, or <span className="font-medium text-slate-700">Intro to Nursing</span>.
        </p>
      </div>
      <Combobox
        placeholder="Search or add a class..."
        value={q}
        onChange={setQ}
        results={items}
        loading={search.isLoading}
        autoOpen
        emptyLabel="No classes found — type to add one"
        mobileInlineResults
        onSelect={(item) => onSelect({ id: item.id, schoolId: school.id, code: item.label, name: (item.sublabel ?? '').replace(/ \(popular\)$/, '') || null })}
        onCreate={async () => {
          const r = await create.mutateAsync({ schoolId: school.id, code: q.trim().toUpperCase() });
          if (r.class) onSelect(r.class);
        }}
        createLabel="Add class"
      />
    </div>
  );
}

// ── Step 3: Professor ───────────────────────────────────────────────────────

function ProfessorStep({ school, onSelect, onBack }: {
  school: School;
  onSelect: (p: Professor | null, allTeachers: boolean) => void;
  onBack: () => void;
}) {
  const [q, setQ] = useState('');
  const search = useProfessorSearch(school.id, q);
  const create = useCreateProfessor();

  const items: ComboboxItem[] = (search.data?.professors ?? []).map((p) => ({
    id: p.id, label: p.name,
  }));

  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-slate-600">&larr; Back</button>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">Who's your professor?</h1>
        <p className="text-slate-500 mt-2">This is optional. Skip if you're not sure.</p>
      </div>
      <Combobox
        placeholder="Search professor name..."
        value={q}
        onChange={setQ}
        results={items}
        loading={search.isLoading}
        mobileInlineResults
        onSelect={(item) => onSelect({ id: item.id, schoolId: school.id, name: item.label, verified: false }, false)}
        onCreate={async () => {
          const r = await create.mutateAsync({ schoolId: school.id, name: q.trim() });
          if (r.professor) onSelect(r.professor, false);
        }}
        createLabel="Add professor"
      />
      <button
        onClick={() => onSelect(null, true)}
        className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-500 font-medium hover:border-slate-400 hover:text-slate-600 transition-colors active:scale-[0.98]"
      >
        Skip &mdash; optional
      </button>
    </div>
  );
}

// ── Step 4: Year ────────────────────────────────────────────────────────────

function YearStep({ year, onSelect, onBack }: {
  year: number | null;
  onSelect: (y: number) => void;
  onBack: () => void;
}) {
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-slate-600">&larr; Back</button>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">What year?</h1>
        <p className="text-slate-500 mt-2">Select the year you're taking this class.</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        {YEARS.map(y => (
          <button
            key={y}
            onClick={() => onSelect(y)}
            className={`py-4 rounded-xl text-lg font-semibold border-2 transition-all active:scale-[0.97] ${
              year === y
                ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                : 'border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            {y}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Step 5: Term ────────────────────────────────────────────────────────────

function TermStep({ term, onSelect, onSkip, onBack, loading }: {
  term: string | null;
  onSelect: (t: string) => void;
  onSkip: () => void;
  onBack: () => void;
  loading: boolean;
}) {
  return (
    <div className="space-y-6">
      <button onClick={onBack} className="text-sm text-slate-400 hover:text-slate-600">&larr; Back</button>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">What term?</h1>
        <p className="text-slate-500 mt-2">This is optional.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        {TERMS.map(t => (
          <button
            key={t}
            onClick={() => onSelect(t)}
            disabled={loading}
            className={`py-4 rounded-xl text-base font-semibold border-2 transition-all active:scale-[0.97] disabled:opacity-50 ${
              term === t
                ? 'border-squishy-primary bg-teal-50 text-squishy-primary'
                : 'border-slate-200 text-slate-700 hover:border-slate-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>
      <button
        onClick={onSkip}
        disabled={loading}
        className="w-full py-3 border-2 border-dashed border-slate-300 rounded-xl text-sm text-slate-500 font-medium hover:border-slate-400 hover:text-slate-600 transition-colors active:scale-[0.98] disabled:opacity-50"
      >
        {loading ? 'Setting up...' : 'Skip \u2014 optional'}
      </button>
    </div>
  );
}

// ── Step 6: Done ────────────────────────────────────────────────────────────

function DoneStep({ sectionId, classCode }: { sectionId: string; classCode: string }) {
  const nav = useNavigate();
  // Get question count for this section via the feed endpoint
  const { data: feedData, isLoading } = useFeedCount(sectionId);
  const questionCount = feedData?.count ?? 0;

  return (
    <div className="space-y-6 text-center">
      <div className="text-5xl">&#x1F389;</div>
      <div>
        <h1 className="text-2xl font-bold text-squishy-dark">You're all set!</h1>
        <p className="text-slate-500 mt-2"><span className="font-semibold text-squishy-dark">{classCode}</span> has been added to your classes.</p>
      </div>

      <div className="space-y-3 pt-2">
        <button
          onClick={() => nav(`/class/${sectionId}`)}
          className="w-full bg-gradient-to-br from-squishy-primary to-teal-600 text-white py-4 rounded-2xl font-bold text-lg shadow-lg active:scale-[0.97] transition-transform"
        >
          Start Studying
          {!isLoading && questionCount > 0 && (
            <span className="block text-sm font-normal opacity-80 mt-0.5">
              {questionCount} question{questionCount !== 1 ? 's' : ''} ready
            </span>
          )}
          {!isLoading && questionCount === 0 && (
            <span className="block text-sm font-normal opacity-80 mt-0.5">
              Add questions first, or jump in
            </span>
          )}
        </button>

        <button
          onClick={() => nav('/upload')}
          className="w-full border-2 border-slate-200 text-slate-700 py-4 rounded-2xl font-semibold text-base hover:border-slate-300 transition-colors active:scale-[0.97]"
        >
          Add Questions
          <span className="block text-sm font-normal text-slate-500 mt-0.5">
            Upload notes, slides, or paste text
          </span>
        </button>
      </div>
    </div>
  );
}

// Quick hook to get question count for a class section
import { useQuestionsFeed } from '../lib/api';
function useFeedCount(sectionId: string) {
  const { data, isLoading } = useQuestionsFeed({ class_section_id: sectionId });
  const count = (data?.questions ?? []).filter((q: any) => q.type !== 'flashcard' && q.type !== 'quick_mcq').length;
  return { data: { count }, isLoading };
}
